import { useEffect, useState } from 'react';
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import {
  SubscriptionStatus,
  tenantIdFromUsageDayPath,
  type Currency,
  type DailyHealth,
  DailyHealthSchema,
  type HealthSignalKind,
} from '@etiquetachef/types';
import { firestore } from '@/lib/firebase';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import {
  directMonthlyRevenue,
  parseSubscriptionDoc,
  parseSubscriptionDocs,
  rankTopDirectRevenue,
  reportSubscriptionDrops,
  type SubscriptionDrop,
} from '@/features/dashboard-finance/subscriptionRevenue';
import {
  isReportExcludedTenant,
  withoutTestTenantEntries,
} from '@/features/tenants/testTenantFilter';
import { serverNow } from '@etiquetachef/ui-shared/online';

const TAG = 'admin/customers/useCustomers';

export type CustomerRow = {
  tenantId: string;
  tenantName: string;
  countryCode: string;
  currency: Currency | null;
  priceCents: number;
  usdEqCents: number;
  metricLabel: string;
  metricValue: string;
};

export type CustomersState =
  | { kind: 'LOADING' }
  | { kind: 'ERROR'; error: string }
  | { kind: 'READY'; rows: CustomerRow[] };

const ROW_LIMIT = 50;

type TenantInfo = {
  name: string;
  countryCode: string;
  priceCents: number;
  currency: Currency | null;
  usdEqCents: number;
  /** Tenant de teste pela assinatura lida aqui (`livemode: false`, #1008). */
  isTest: boolean;
};

/**
 * Nome, país e receita direta mensal de cada tenant das linhas de uma aba.
 *
 * Origem: sem issue — commit fd8155bc4, 2026-05-04 (rota /dashboard/customers).
 *
 * Contrato: lê `tenants/{id}` (lote de 30 por `in`) e
 * `tenants/{id}/billing/subscription` (um `getDoc` por tenant). Preço =
 * receita DIRETA mensal (`directMonthlyRevenue`); cortesia, demo, parceiro,
 * test mode e tenant sem assinatura saem com `currency: null` e 0 — a tela
 * mostra "—" em vez de um preço que não é cobrado. Doc de assinatura fora do
 * schema vai para `drops` (o hook reporta uma vez). `isTest` (#1008) =
 * `isReportExcludedTenant` com a assinatura já lida — as abas de uso o usam
 * para tirar o sandbox não cadastrado, que o filtro só por id não vê.
 *
 * Usado por: todas as abas de `useCustomers` (coluna Preço nativo / USD eq.).
 *
 * Falhas conhecidas:
 *  - 2026-05-19 → 2026-10-03 (#795, ADM-4): lia `billing/current` +
 *    `priceCents` (órfão) — todo tenant saía com preço "—".
 *
 * Histórico:
 *  - 2026-10-03 14:12 +0200 — (#795, ADM-4) passa a ler `billing/subscription`.
 *  - 2026-10-19 20:57 +0200 — (#1008) campo `isTest`.
 */
async function loadTenantInfoMap(
  tenantIds: readonly string[],
  drops: SubscriptionDrop[],
): Promise<Map<string, TenantInfo>> {
  if (tenantIds.length === 0) return new Map();
  const out = new Map<string, TenantInfo>();
  for (let i = 0; i < tenantIds.length; i += 30) {
    const slice = tenantIds.slice(i, i + 30);
    const tenantsSnap = await getDocs(
      query(collection(firestore, 'tenants'), where('__name__', 'in', slice)),
    );
    // Bug corrigido em 2026-10-03 14:12 +0200 (#795, ADM-4): consultava
    // `billing` com `__name__ == 'current'` — doc órfão desde 2026-05-19
    // (`e9692f87e`), então todo tenant saía sem preço. A fonte é o singleton
    // `billing/subscription`, lido pela régua de `subscriptionRevenue.ts`.
    const subSnaps = await Promise.all(
      slice.map((id) => getDoc(doc(firestore, 'tenants', id, 'billing', 'subscription'))),
    );
    const revenueById = new Map<string, ReturnType<typeof directMonthlyRevenue>>();
    const testIds = new Set<string>();
    for (const snap of subSnaps) {
      if (!snap.exists()) continue;
      const parsed = parseSubscriptionDoc(snap.ref.path, snap.data());
      if (parsed === null) continue;
      if (!parsed.ok) {
        drops.push(parsed.drop);
        continue;
      }
      revenueById.set(parsed.tenantId, directMonthlyRevenue(parsed.fields));
      if (isReportExcludedTenant(parsed.tenantId, parsed.fields)) testIds.add(parsed.tenantId);
    }
    for (const t of tenantsSnap.docs) {
      const data = t.data() as Record<string, unknown>;
      const revenue = revenueById.get(t.id) ?? null;
      out.set(t.id, {
        name: typeof data.name === 'string' ? data.name : t.id,
        countryCode: typeof data.countryCode === 'string' ? data.countryCode : '',
        priceCents: revenue?.monthlyCents ?? 0,
        currency: revenue?.currency ?? null,
        usdEqCents: revenue?.usdEqCents ?? 0,
        isTest: testIds.has(t.id),
      });
    }
  }
  return out;
}

function tenantIdFromActivityPath(path: string): string | null {
  const m = /^tenants\/([^/]+)\/activity_log\/[^/]+$/.exec(path);
  return m ? (m[1] ?? null) : null;
}

function fmtInt(n: number): string {
  return new Intl.NumberFormat('pt-BR').format(n);
}

function fmtUsd(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export type CustomersTab =
  | 'most-active'
  | 'top-volume'
  | 'top-revenue'
  | 'churn-risk'
  | 'zombie'
  | 'print-failures';

/**
 * Hook unificado para os 6 tabs de `/dashboard/customers`.
 *
 * Origem: sem issue — commit fd8155bc4, 2026-05-04 (rota /dashboard/customers).
 *
 * Contrato: cada tab pega um critério distinto:
 *   most-active    = activity_log nos últimos 30d (collectionGroup).
 *   top-volume     = sum(labelsPrinted) últimos 30d via days collGroup.
 *   top-revenue    = `billing/subscription` ACTIVE, ordenado EM MEMÓRIA pela
 *                    receita direta mensal em USD eq. (`rankTopDirectRevenue`).
 *   churn-risk     = topProblematic com PRE_CHURN_RISK no health.
 *   zombie         = topProblematic com ZOMBIE no health.
 *   print-failures = depende de PostHog (4.8). Hoje retorna [] +
 *                    flag awaitingInstrumentation.
 * Tenant de TESTE (#1008) sai de most-active, top-volume e top-revenue:
 * cadastro antes do ranking (`withoutTestTenantEntries`), sandbox
 * (`livemode: false`) depois de ler a assinatura — a aba pode então mostrar
 * menos de 50 linhas. As abas de health leem o agregado `aggregateHealth`, que
 * é filtrado na Function (#1008 item 5), não aqui.
 * Erro → `ERROR` + `reportNonFatal('admin_customers_load_failed')`; doc de
 * assinatura fora do schema → `admin_subscription_doc_dropped` (uma vez).
 *
 * Usado por: `CustomersDashboardPage.tsx` (web `apps/admin`, super-admin).
 *
 * Arquitetura: receita pela régua única de
 * `dashboard-finance/subscriptionRevenue.ts` (espelho da régua do MRR das
 * Functions, #679 D15). Só leitura. As três collection-group queries são
 * autorizadas em `firebase/firestore.rules` só para `isSuperAdmin()`, e cada
 * uma depende de um índice de `firebase/firestore.indexes.json`:
 *  - most-active: `activity_log` `createdAt >=` → `match
 *    /{path=**}/activity_log/{eventId}` + `fieldOverrides`
 *    `activity_log.createdAt` (escopo COLLECTION_GROUP);
 *  - top-volume: `days` `yyyymmdd >=` → `match /{path=**}/days/{yyyymmdd}` +
 *    `fieldOverrides` `days.yyyymmdd` (COLLECTION_GROUP); o CG casa também
 *    `aggregations/*` e `crm_lookup_quota/*`, e `tenantIdFromUsageDayPath`
 *    descarta o que não é bucket de tenant;
 *  - top-revenue: `billing` `status ==` → `match /{path=**}/billing/{billingDoc}`
 *    + `fieldOverrides` `billing.status` (COLLECTION_GROUP).
 * As abas de health leem `aggregations/health/days` direto (coleção, não CG),
 * último dia por `orderBy('yyyymmdd', 'desc')` — índice automático.
 *
 * Dados (anonimizado):
 *   tenants/{tenantId}/activity_log/{eventId} { "createdAt": 1759300000000, … }
 *   tenants/{tenantId}/billing/usage/days/{yyyymmdd}
 *     { "yyyymmdd": "20261001", "labelsPrinted": 42 }
 *   aggregations/health/days/{yyyymmdd} — `DailyHealthSchema`
 *     (`@etiquetachef/types`): `{ "yyyymmdd": "20261001", "topProblematic":
 *     [{ "tenantId": "tenant-1", "signals": ["ZOMBIE"] }], … }`
 *   tenants/{tenantId}/billing/subscription — centavos por ciclo, régua de
 *     `subscriptionRevenue.ts`.
 *
 * Falhas conhecidas:
 *  - 2026-05-19 → 2026-10-03 (#795, ADM-4): top-revenue lia `billing/current`
 *    + `priceCents` (órfão) — aba vazia e preço "—" em todas as abas.
 *  - `loadFromHealth`: doc de health fora do `DailyHealthSchema` devolve aba
 *    vazia sem report ao PostHog — achado registrado na #926.
 *
 *  - até 2026-10-19 (#1008): os tenants do simulador de uso (IT-98765432103,
 *    BR-01010001000180) ficavam no topo de most-active e top-volume.
 *
 * Issues: #795 (ADM-4), #926 (achados), #1008 (tenant de teste fora).
 *
 * Histórico:
 *  - 2026-10-03 14:12 +0200 — (#795, ADM-4) receita de `billing/subscription`;
 *    falha de carga passa a ir ao PostHog (antes só `console.error`).
 *  - 2026-10-19 20:57 +0200 — (#1008) exclui tenant de teste das abas de uso
 *    e de receita (decisão do owner: o simulador sai de todos os painéis).
 */
export function useCustomers(tab: CustomersTab): CustomersState & {
  awaitingInstrumentation?: boolean;
} {
  const [state, setState] = useState<CustomersState>({ kind: 'LOADING' });

  useEffect(() => {
    const guard = { cancelled: false };
    setState({ kind: 'LOADING' });

    async function load(): Promise<void> {
      const drops: SubscriptionDrop[] = [];
      try {
        const rows = await loadTab(tab, drops);
        if (guard.cancelled) return;
        reportSubscriptionDrops(`customers:${tab}`, drops);
        setState({ kind: 'READY', rows });
      } catch (err) {
        if (guard.cancelled) return;
        console.error(`${TAG}: failed`, err);
        reportNonFatal('admin_customers_load_failed', { tab, ...serializeError(err) });
        setState({
          kind: 'ERROR',
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
    void load();
    return (): void => {
      guard.cancelled = true;
    };
  }, [tab]);

  return tab === 'print-failures'
    ? { ...state, awaitingInstrumentation: true }
    : state;
}

async function loadTab(tab: CustomersTab, drops: SubscriptionDrop[]): Promise<CustomerRow[]> {
  switch (tab) {
    case 'most-active':
      return loadMostActive(drops);
    case 'top-volume':
      return loadTopVolume(drops);
    case 'top-revenue':
      return loadTopRevenue(drops);
    case 'churn-risk':
      return loadFromHealth('PRE_CHURN_RISK', drops);
    case 'zombie':
      return loadFromHealth('ZOMBIE', drops);
    case 'print-failures':
      return [];
  }
}

async function loadMostActive(drops: SubscriptionDrop[]): Promise<CustomerRow[]> {
  const since = serverNow() - THIRTY_DAYS_MS;
  const snap = await getDocs(
    query(
      collectionGroup(firestore, 'activity_log'),
      where('createdAt', '>=', since),
    ),
  );
  const counts = new Map<string, number>();
  for (const d of snap.docs) {
    const tid = tenantIdFromActivityPath(d.ref.path);
    if (!tid) continue;
    counts.set(tid, (counts.get(tid) ?? 0) + 1);
  }
  // #1008: tenant de teste fora antes do ranking (cadastro) e depois da
  // leitura da assinatura (sandbox).
  const sorted = withoutTestTenantEntries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, ROW_LIMIT);
  const tenantIds = sorted.map(([id]) => id);
  const info = await loadTenantInfoMap(tenantIds, drops);
  return sorted
    .filter(([id]) => info.get(id)?.isTest !== true)
    .map(([id, count]) => makeRow(id, info, 'actions', fmtInt(count)));
}

async function loadTopVolume(drops: SubscriptionDrop[]): Promise<CustomerRow[]> {
  const since = serverNow() - THIRTY_DAYS_MS;
  const sinceYyyymmdd = new Date(since)
    .toISOString()
    .slice(0, 10)
    .replace(/-/g, '');
  const snap = await getDocs(
    query(
      collectionGroup(firestore, 'days'),
      where('yyyymmdd', '>=', sinceYyyymmdd),
    ),
  );
  const totals = new Map<string, number>();
  for (const d of snap.docs) {
    const tid = tenantIdFromUsageDayPath(d.ref.path);
    if (!tid) continue;
    const n = (d.get('labelsPrinted') as number | undefined) ?? 0;
    totals.set(tid, (totals.get(tid) ?? 0) + n);
  }
  // #1008: o simulador de uso imprime todo dia e ficava no topo — fora antes
  // do ranking (cadastro) e depois da leitura da assinatura (sandbox).
  const sorted = withoutTestTenantEntries(totals)
    .sort((a, b) => b[1] - a[1])
    .slice(0, ROW_LIMIT);
  const tenantIds = sorted.map(([id]) => id);
  const info = await loadTenantInfoMap(tenantIds, drops);
  return sorted
    .filter(([id]) => info.get(id)?.isTest !== true)
    .map(([id, count]) => makeRow(id, info, 'labels', fmtInt(count)));
}

async function loadTopRevenue(drops: SubscriptionDrop[]): Promise<CustomerRow[]> {
  // Bug corrigido em 2026-10-03 14:12 +0200 (#795, ADM-4): era
  // `orderBy('priceCents')` sobre `billing/current`, doc órfão desde
  // 2026-05-19 — a aba ficava vazia. Sem `orderBy` de propósito: a receita
  // mensal em USD depende de ciclo e moeda, que nenhum campo indexável
  // carrega; o ranking é em memória sobre todas as assinaturas ACTIVE.
  const billingSnap = await getDocs(
    query(
      collectionGroup(firestore, 'billing'),
      where('status', '==', SubscriptionStatus.ACTIVE),
    ),
  );
  // #1008: tenant de teste fora antes do ranking — a assinatura já está aqui.
  const subs = new Map(
    Array.from(parseSubscriptionDocs(billingSnap.docs, drops)).filter(
      ([tid, f]) => !isReportExcludedTenant(tid, f),
    ),
  );
  const top = rankTopDirectRevenue(subs, ROW_LIMIT);
  const info = await loadTenantInfoMap(top.map((i) => i.tenantId), drops);
  return top.map((i) => makeRow(i.tenantId, info, 'mrr', fmtUsd(i.usdEqCents)));
}

async function loadFromHealth(
  kind: HealthSignalKind,
  drops: SubscriptionDrop[],
): Promise<CustomerRow[]> {
  const snap = await getDocs(
    query(
      collection(firestore, 'aggregations', 'health', 'days'),
      orderBy('yyyymmdd', 'desc'),
      limit(1),
    ),
  );
  const latest = snap.docs[0];
  if (!latest) return [];
  const parsed = DailyHealthSchema.safeParse(latest.data());
  if (!parsed.success) return [];
  const health: DailyHealth = parsed.data;
  const tenantIds = health.topProblematic
    .filter((s) => s.signals.includes(kind))
    .map((s) => s.tenantId)
    .slice(0, ROW_LIMIT);
  if (tenantIds.length === 0) return [];
  const info = await loadTenantInfoMap(tenantIds, drops);
  return tenantIds.map((id) => makeRow(id, info, 'signals', kind));
}

function makeRow(
  tenantId: string,
  info: Map<string, TenantInfo>,
  metricLabel: string,
  metricValue: string,
): CustomerRow {
  const t = info.get(tenantId);
  return {
    tenantId,
    tenantName: t?.name ?? tenantId,
    countryCode: t?.countryCode ?? '',
    currency: t?.currency ?? null,
    priceCents: t?.priceCents ?? 0,
    usdEqCents: t?.usdEqCents ?? 0,
    metricLabel,
    metricValue,
  };
}
